/**
 * Feedback Management - Admin Page
 * จัดการ feedback และข้อเสนอแนะจากผู้ใช้
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiMessageSquare,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiThumbsUp,
  FiThumbsDown,
  FiStar,
  FiMail,
  FiUser,
  FiCalendar,
  FiX,
  FiInfo
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { AdminLayout } from '../../components/admin';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { API_URL } from '../../services/api';

// Types based on Backend Models
enum FeedbackCategory {
  BUG_REPORT = 'BUG_REPORT',
  FEATURE_REQUEST = 'FEATURE_REQUEST',
  GENERAL_FEEDBACK = 'GENERAL_FEEDBACK',
  COMPLAINT = 'COMPLAINT',
  COMPLIMENT = 'COMPLIMENT',
  OTHER = 'OTHER'
}

enum FeedbackStatus {
  NEW = 'NEW',
  READ = 'READ',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED'
}

interface Feedback {
  id: string;
  userId?: string;
  subject: string;
  category: FeedbackCategory;
  message: string;
  rating?: string;
  email?: string;
  name?: string;
  status: FeedbackStatus;
  adminNotes?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface FeedbackListResponse {
  feedbacks: Feedback[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface FeedbackStats {
  total: number;
  byStatus: Record<string, number>;
  byCategory: Record<string, number>;
  recentCount: number;
  unreadCount: number;
}

const FeedbackManagement: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();

  // Custom API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    
    const response = await fetch(`${API_URL}${options.endpoint}`, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.detail || result.message || `HTTP ${response.status}`);
    }

    // Backend returns data directly for some endpoints
    return {
      success: true,
      data: result.data || result,
      message: result.message
    };
  }, [token]);

  // State
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<FeedbackCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all');

  // Load feedbacks
  const loadFeedbacks = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      clearError();

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(categoryFilter !== 'all' && { category_filter: categoryFilter }),
        ...(statusFilter !== 'all' && { status_filter: statusFilter })
      });

      const response = await callApi<FeedbackListResponse>({
        method: 'GET',
        endpoint: `/api/feedback/admin/list?${params.toString()}`
      });

      if (response.success && response.data) {
        setFeedbacks(response.data.feedbacks || []);
        setPagination({
          total: response.data.total || 0,
          page: response.data.page || 1,
          pageSize: response.data.pageSize || 20,
          totalPages: response.data.totalPages || 0
        });
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถโหลดรายการ feedback ได้');
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter, statusFilter, token, callApi, handleError, clearError]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await callApi<FeedbackStats>({
        method: 'GET',
        endpoint: '/api/feedback/admin/stats/summary'
      });

      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [token, callApi]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadFeedbacks(1);
      loadStats();
    }
  }, [token]);

  // Reload when filters change (debounced for search)
  useEffect(() => {
    if (!token) return;

    const timer = setTimeout(() => {
      loadFeedbacks(1);
    }, searchTerm ? 500 : 0); // Debounce search only

    return () => clearTimeout(timer);
  }, [searchTerm, categoryFilter, statusFilter]);

  // Handle status change
  const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
    try {
      const response = await callApi({
        method: 'PUT',
        endpoint: `/api/feedback/admin/${feedbackId}/status?new_status=${newStatus}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ''}`
      });

      if (response.success) {
        toast.success('เปลี่ยนสถานะเรียบร้อยแล้ว');
        loadFeedbacks(pagination.page);
        loadStats();
        setShowDetailModal(false);
        setAdminNotes('');
      }
    } catch (error: any) {
      toast.error(error.message || 'ไม่สามารถเปลี่ยนสถานะได้');
    }
  };

  // View feedback detail
  const viewFeedbackDetail = async (feedback: Feedback) => {
    try {
      const response = await callApi<Feedback>({
        method: 'GET',
        endpoint: `/api/feedback/admin/${feedback.id}`
      });

      if (response.success && response.data) {
        setSelectedFeedback(response.data);
        setAdminNotes(response.data.adminNotes || '');
        setShowDetailModal(true);
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถโหลดรายละเอียด feedback ได้');
    }
  };

  // Category badge
  const CategoryBadge: React.FC<{ category: FeedbackCategory }> = ({ category }) => {
    const configs = {
      [FeedbackCategory.BUG_REPORT]: {
        color: 'bg-red-100 text-red-800',
        icon: FiAlertCircle,
        label: 'แจ้งปัญหา'
      },
      [FeedbackCategory.FEATURE_REQUEST]: {
        color: 'bg-blue-100 text-blue-800',
        icon: FiStar,
        label: 'ขอฟีเจอร์ใหม่'
      },
      [FeedbackCategory.GENERAL_FEEDBACK]: {
        color: 'bg-gray-100 text-gray-800',
        icon: FiMessageSquare,
        label: 'ข้อเสนอแนะทั่วไป'
      },
      [FeedbackCategory.COMPLAINT]: {
        color: 'bg-orange-100 text-orange-800',
        icon: FiThumbsDown,
        label: 'ร้องเรียน'
      },
      [FeedbackCategory.COMPLIMENT]: {
        color: 'bg-green-100 text-green-800',
        icon: FiThumbsUp,
        label: 'ชื่นชม'
      },
      [FeedbackCategory.OTHER]: {
        color: 'bg-purple-100 text-purple-800',
        icon: FiInfo,
        label: 'อื่นๆ'
      }
    };

    const config = configs[category];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="mr-1 h-3 w-3" />
        {config.label}
      </span>
    );
  };

  // Status badge
  const StatusBadge: React.FC<{ status: FeedbackStatus }> = ({ status }) => {
    const configs = {
      [FeedbackStatus.NEW]: {
        color: 'bg-red-100 text-red-800',
        icon: FiAlertCircle,
        label: 'ใหม่'
      },
      [FeedbackStatus.READ]: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: FiEye,
        label: 'อ่านแล้ว'
      },
      [FeedbackStatus.IN_PROGRESS]: {
        color: 'bg-blue-100 text-blue-800',
        icon: FiClock,
        label: 'กำลังดำเนินการ'
      },
      [FeedbackStatus.RESOLVED]: {
        color: 'bg-green-100 text-green-800',
        icon: FiCheckCircle,
        label: 'แก้ไขแล้ว'
      },
      [FeedbackStatus.CLOSED]: {
        color: 'bg-gray-100 text-gray-800',
        icon: FiXCircle,
        label: 'ปิดเรื่อง'
      }
    };

    const config = configs[status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="mr-1 h-3 w-3" />
        {config.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <AdminLayout>
      <div className="p-3 sm:p-4 md:p-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                <FiMessageSquare className="mr-2 sm:mr-3 h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                จัดการ Feedback
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                ตรวจสอบและตอบกลับ feedback จากผู้ใช้งาน ({pagination.total.toLocaleString()} รายการ)
              </p>
            </div>
            <div className="flex space-x-2 sm:space-x-3">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<FiFilter />}
                onClick={() => setShowFilters(!showFilters)}
              >
                <span className="hidden sm:inline">{showFilters ? 'ซ่อนตัวกรอง' : 'แสดงตัวกรอง'}</span>
                <span className="sm:hidden">ตัวกรอง</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<FiRefreshCw />}
                onClick={() => {
                  loadFeedbacks(pagination.page);
                  loadStats();
                }}
                disabled={loading}
              >
                <span className="hidden sm:inline">รีเฟรช</span>
                <span className="sm:hidden">
                  <FiRefreshCw />
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorState.message && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="font-medium">เกิดข้อผิดพลาด</p>
            <p className="text-sm">{errorState.message}</p>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
            {/* Total */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-purple-100">
                      <FiMessageSquare className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">ทั้งหมด</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* New */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-red-100">
                      <FiAlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">ใหม่</p>
                    <p className="text-2xl font-bold text-red-600">{stats.byStatus?.NEW || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Read */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-yellow-100">
                      <FiEye className="h-6 w-6 text-yellow-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">อ่านแล้ว</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.byStatus?.READ || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* In Progress */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100">
                      <FiClock className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">กำลังดำเนินการ</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.byStatus?.IN_PROGRESS || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resolved */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-green-100">
                      <FiCheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">แก้ไขแล้ว</p>
                    <p className="text-2xl font-bold text-green-600">{stats.byStatus?.RESOLVED || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="ค้นหาด้วย subject หรือ message..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Category Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as FeedbackCategory | 'all')}
              >
                <option value="all">หมวดหมู่ทั้งหมด</option>
                <option value={FeedbackCategory.BUG_REPORT}>แจ้งปัญหา</option>
                <option value={FeedbackCategory.FEATURE_REQUEST}>ขอฟีเจอร์ใหม่</option>
                <option value={FeedbackCategory.GENERAL_FEEDBACK}>ข้อเสนอแนะทั่วไป</option>
                <option value={FeedbackCategory.COMPLAINT}>ร้องเรียน</option>
                <option value={FeedbackCategory.COMPLIMENT}>ชื่นชม</option>
                <option value={FeedbackCategory.OTHER}>อื่นๆ</option>
              </select>

              {/* Status Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | 'all')}
              >
                <option value="all">สถานะทั้งหมด</option>
                <option value={FeedbackStatus.NEW}>ใหม่</option>
                <option value={FeedbackStatus.READ}>อ่านแล้ว</option>
                <option value={FeedbackStatus.IN_PROGRESS}>กำลังดำเนินการ</option>
                <option value={FeedbackStatus.RESOLVED}>แก้ไขแล้ว</option>
                <option value={FeedbackStatus.CLOSED}>ปิดเรื่อง</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
            <span className="ml-2 text-gray-600">กำลังโหลด...</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && feedbacks.length === 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="text-center py-12">
                <FiMessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all'
                    ? 'ไม่พบ feedback ที่ค้นหา'
                    : 'ยังไม่มี feedback'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all'
                    ? 'ลองเปลี่ยนคำค้นหาหรือตัวกรอง'
                    : 'feedback จากผู้ใช้จะแสดงที่นี่'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mobile Card View - แสดงเมื่อ < 768px */}
        {!loading && feedbacks.length > 0 && (
          <div className="md:hidden space-y-4">
            {feedbacks.map((feedback, index) => (
              <motion.div
                key={feedback.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="p-4">
                  {/* Subject & Message */}
                  <div className="mb-3">
                    <div className="flex items-start">
                      {feedback.status === FeedbackStatus.NEW && (
                        <span className="flex h-2 w-2 mr-2 mt-1.5">
                          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                        </span>
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm text-gray-900">{feedback.subject}</h3>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{feedback.message}</p>
                      </div>
                    </div>
                  </div>

                  {/* Category & Status Badges */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <CategoryBadge category={feedback.category} />
                    <StatusBadge status={feedback.status} />
                  </div>

                  {/* Sender Info */}
                  <div className="mb-3 space-y-1">
                    <div className="flex items-center gap-1 text-gray-600 text-sm">
                      <FiUser className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{feedback.name || 'ไม่ระบุชื่อ'}</span>
                    </div>
                    {feedback.email && (
                      <div className="flex items-center gap-1 text-gray-500 text-xs">
                        <FiMail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{feedback.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div className="text-xs text-gray-500 mb-3">
                    <FiCalendar className="inline h-3 w-3 mr-1" />
                    {formatDate(feedback.createdAt)}
                  </div>

                  {/* Action Button */}
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => viewFeedbackDetail(feedback)}
                  >
                    <FiEye className="mr-2 h-4 w-4" /> ดูรายละเอียด
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Desktop Table View - แสดงเมื่อ >= 768px */}
        {!loading && feedbacks.length > 0 && (
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          หัวเรื่อง
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          หมวดหมู่
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          สถานะ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ผู้ส่ง
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          วันที่สร้าง
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          การจัดการ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {feedbacks.map((feedback, index) => (
                        <motion.tr
                          key={feedback.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="hover:bg-gray-50"
                        >
                          {/* Subject & Message */}
                          <td className="px-6 py-4">
                            <div className="max-w-md">
                              <div className="text-sm font-medium text-gray-900 flex items-center">
                                {feedback.status === FeedbackStatus.NEW && (
                                  <span className="flex h-2 w-2 mr-2">
                                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                  </span>
                                )}
                                {feedback.subject}
                              </div>
                              <div className="text-sm text-gray-500 truncate mt-1">
                                {feedback.message}
                              </div>
                            </div>
                          </td>

                          {/* Category */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <CategoryBadge category={feedback.category} />
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <StatusBadge status={feedback.status} />
                          </td>

                          {/* Sender */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900 flex items-center">
                                <FiUser className="h-4 w-4 mr-1 text-gray-400" />
                                {feedback.name || 'ไม่ระบุชื่อ'}
                              </div>
                              <div className="text-gray-500 flex items-center mt-1">
                                <FiMail className="h-4 w-4 mr-1 text-gray-400" />
                                {feedback.email || '-'}
                              </div>
                            </div>
                          </td>

                          {/* Created Date */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <FiCalendar className="h-4 w-4 mr-1 text-gray-400" />
                              {formatDate(feedback.createdAt)}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => viewFeedbackDetail(feedback)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="ดูรายละเอียด"
                            >
                              <FiEye className="h-4 w-4 mr-1" />
                              ดูรายละเอียด
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pagination - แสดงทั้ง mobile และ desktop */}
          {!loading && feedbacks.length > 0 && pagination.totalPages > 1 && (
            <Card className="mt-4">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="text-sm text-gray-700 text-center sm:text-left">
                    แสดง {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} จาก {pagination.total} รายการ
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => loadFeedbacks(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      ก่อนหน้า
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          return page === 1 ||
                                 page === pagination.totalPages ||
                                 (page >= pagination.page - 1 && page <= pagination.page + 1);
                        })
                        .map((page, index, array) => {
                          if (index > 0 && array[index - 1] !== page - 1) {
                            return (
                              <React.Fragment key={`ellipsis-${page}`}>
                                <span className="px-2 text-gray-500">...</span>
                                <button
                                  onClick={() => loadFeedbacks(page)}
                                  className={`px-3 py-1 rounded ${
                                    pagination.page === page
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          }
                          return (
                            <button
                              key={page}
                              onClick={() => loadFeedbacks(page)}
                              className={`px-3 py-1 rounded ${
                                pagination.page === page
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => loadFeedbacks(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      ถัดไป
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        {/* Detail Modal */}
        {showDetailModal && selectedFeedback && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                onClick={() => setShowDetailModal(false)}
              />

              {/* Modal panel */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                {/* Header */}
                <div className="bg-blue-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-white flex items-center">
                      <FiMessageSquare className="mr-2 h-5 w-5" />
                      รายละเอียด Feedback
                    </h3>
                    <button
                      onClick={() => setShowDetailModal(false)}
                      className="text-white hover:text-gray-200"
                    >
                      <FiX className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">หัวเรื่อง</label>
                    <p className="text-base text-gray-900">{selectedFeedback.subject}</p>
                  </div>

                  {/* Category & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">หมวดหมู่</label>
                      <CategoryBadge category={selectedFeedback.category} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">สถานะปัจจุบัน</label>
                      <StatusBadge status={selectedFeedback.status} />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ข้อความ</label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedFeedback.message}</p>
                    </div>
                  </div>

                  {/* Rating */}
                  {selectedFeedback.rating && !isNaN(parseInt(selectedFeedback.rating)) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">คะแนน</label>
                      <div className="flex items-center">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <FiStar
                            key={i}
                            className={`h-5 w-5 ${
                              i < parseInt(selectedFeedback.rating || '0')
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="ml-2 text-sm text-gray-600">
                          ({selectedFeedback.rating}/5)
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Sender Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อผู้ส่ง</label>
                      <p className="text-sm text-gray-900">{selectedFeedback.name || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">อีเมล</label>
                      <p className="text-sm text-gray-900">{selectedFeedback.email || '-'}</p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">วันที่สร้าง</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedFeedback.createdAt)}</p>
                    </div>
                    {selectedFeedback.resolvedAt && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">วันที่แก้ไข</label>
                        <p className="text-sm text-gray-900">{formatDate(selectedFeedback.resolvedAt)}</p>
                      </div>
                    )}
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">หมายเหตุของ Admin</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      placeholder="เขียนหมายเหตุหรือคำอธิบายการแก้ไข..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                    />
                  </div>

                  {/* Status Actions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">เปลี่ยนสถานะ</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedFeedback.status !== FeedbackStatus.READ && (
                        <button
                          onClick={() => handleStatusChange(selectedFeedback.id, FeedbackStatus.READ)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <FiEye className="mr-1 h-4 w-4" />
                          ทำเครื่องหมายว่าอ่านแล้ว
                        </button>
                      )}
                      {selectedFeedback.status !== FeedbackStatus.IN_PROGRESS && (
                        <button
                          onClick={() => handleStatusChange(selectedFeedback.id, FeedbackStatus.IN_PROGRESS)}
                          className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                        >
                          <FiClock className="mr-1 h-4 w-4" />
                          เริ่มดำเนินการ
                        </button>
                      )}
                      {selectedFeedback.status !== FeedbackStatus.RESOLVED && (
                        <button
                          onClick={() => handleStatusChange(selectedFeedback.id, FeedbackStatus.RESOLVED)}
                          className="inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                        >
                          <FiCheckCircle className="mr-1 h-4 w-4" />
                          แก้ไขเรียบร้อย
                        </button>
                      )}
                      {selectedFeedback.status !== FeedbackStatus.CLOSED && (
                        <button
                          onClick={() => handleStatusChange(selectedFeedback.id, FeedbackStatus.CLOSED)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <FiXCircle className="mr-1 h-4 w-4" />
                          ปิดเรื่อง
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    ปิด
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FeedbackManagement;
